/**
 * ConversationLinker — Enhanced Flow
 * 
 * After each AI response, guides user through:
 * 1. Link to Client? → If yes, select/create client
 * 2. Link to Case? → If yes, select/create case under that client
 * 3. Upload documents? → If yes, file upload for the case
 * 4. Skip → Save as general thread
 */

import React, { useState, useRef } from 'react';
import { Link2, Plus, X, MessageSquare, Upload, FolderOpen, Users } from 'lucide-react';

interface Client { id: string; name: string; }
interface Case { id: string; title: string; clientName: string; clientRecordId?: string; }

interface ConversationLinkerProps {
  clients: Client[];
  cases: Case[];
  onLink: (type: 'client' | 'case', id: string) => void;
  onCreateClient: (name: string) => void;
  onCreateCase: (title: string, clientId: string) => void;
  onUploadDocument?: (caseId: string, file: File) => void;
  onSkip: () => void;
}

type Step = 'prompt' | 'select-client' | 'new-client' | 'ask-case' | 'select-case' | 'new-case' | 'ask-document' | 'done';

const ConversationLinker: React.FC<ConversationLinkerProps> = ({
  clients, cases, onLink, onCreateClient, onCreateCase, onUploadDocument, onSkip
}) => {
  const [step, setStep] = useState<Step>('prompt');
  const [newName, setNewName] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [linkedCaseId, setLinkedCaseId] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const clientCases = selectedClientId 
    ? cases.filter(c => c.clientRecordId === selectedClientId || c.clientName === clients.find(cl => cl.id === selectedClientId)?.name)
    : cases;

  const handleLinkClient = (clientId: string) => {
    setSelectedClientId(clientId);
    onLink('client', clientId);
    // Ask if they want to link to a case too
    setStep('ask-case');
  };

  const handleCreateClient = () => {
    if (newName.trim()) {
      onCreateClient(newName.trim());
      setSelectedClientId('new'); // Will be resolved by parent
      setNewName('');
      setStep('ask-case');
    }
  };

  const handleLinkCase = (caseId: string) => {
    setLinkedCaseId(caseId);
    onLink('case', caseId);
    // Ask if they want to upload documents
    if (onUploadDocument) {
      setStep('ask-document');
    } else {
      setStep('done');
      setTimeout(onSkip, 500);
    }
  };

  const handleCreateCase = () => {
    if (newName.trim()) {
      onCreateCase(newName.trim(), selectedClientId);
      setNewName('');
      if (onUploadDocument) {
        setStep('ask-document');
      } else {
        setStep('done');
        setTimeout(onSkip, 500);
      }
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onUploadDocument && linkedCaseId) {
      onUploadDocument(linkedCaseId, file);
    }
    e.target.value = '';
  };

  return (
    <div className="mx-4 mb-3 p-3 bg-indigo-50 border border-indigo-200 rounded-xl animate-in fade-in">
      {/* Step 1: Initial prompt */}
      {step === 'prompt' && (
        <div className="flex items-center gap-2 flex-wrap">
          <Link2 size={13} className="text-indigo-500" />
          <span className="text-[11px] text-indigo-700 font-medium">Save this conversation?</span>
          <div className="flex gap-1.5 ml-auto">
            <button onClick={() => setStep('select-client')} className="px-2.5 py-1 bg-indigo-600 text-white rounded-lg text-[10px] font-semibold hover:bg-indigo-700 flex items-center gap-1">
              <Users size={10} /> Link to Client
            </button>
            <button onClick={() => setStep('select-case')} className="px-2.5 py-1 bg-blue-600 text-white rounded-lg text-[10px] font-semibold hover:bg-blue-700 flex items-center gap-1">
              <FolderOpen size={10} /> Link to Case
            </button>
            <button onClick={onSkip} className="px-2.5 py-1 bg-white border border-gray-200 text-gray-600 rounded-lg text-[10px] font-medium hover:bg-gray-50">
              Save as General
            </button>
          </div>
        </div>
      )}

      {/* Step 2a: Select existing client */}
      {step === 'select-client' && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-indigo-700 font-semibold flex items-center gap-1"><Users size={11} /> Select or create client:</span>
            <button onClick={() => setStep('prompt')} className="text-gray-400 hover:text-gray-600"><X size={12} /></button>
          </div>

          {clients.length > 0 && (
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {clients.map(c => (
                <button key={c.id} onClick={() => handleLinkClient(c.id)}
                  className="w-full text-left px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg text-[10px] hover:border-indigo-300 hover:bg-indigo-50 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-[9px] text-indigo-600 font-bold">{c.name[0]}</span>
                  <span className="font-medium text-gray-700">{c.name}</span>
                </button>
              ))}
            </div>
          )}

          <button onClick={() => setStep('new-client')} className="w-full px-2 py-1.5 bg-green-50 border border-green-200 text-green-700 rounded-lg text-[10px] font-medium hover:bg-green-100 flex items-center justify-center gap-1">
            <Plus size={10} /> Create New Client
          </button>
        </div>
      )}

      {/* Step 2b: Create new client */}
      {step === 'new-client' && (
        <div className="space-y-2">
          <div className="text-[11px] text-green-700 font-semibold">Create new client:</div>
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Client name"
            className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-[11px]" autoFocus
            onKeyDown={e => { if (e.key === 'Enter') handleCreateClient(); }} />
          <div className="flex gap-1.5">
            <button onClick={handleCreateClient}
              className="px-3 py-1 bg-green-600 text-white rounded-lg text-[10px] font-semibold">Create & Link</button>
            <button onClick={() => setStep('select-client')} className="px-3 py-1 text-gray-500 text-[10px]">Back</button>
          </div>
        </div>
      )}

      {/* Step 3: Ask about case */}
      {step === 'ask-case' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-blue-700 font-medium">✅ Linked to client. Add to a specific case?</span>
          </div>
          <div className="flex gap-1.5">
            <button onClick={() => setStep('select-case')} className="px-2.5 py-1 bg-blue-600 text-white rounded-lg text-[10px] font-semibold hover:bg-blue-700 flex items-center gap-1">
              <FolderOpen size={10} /> Yes, select case
            </button>
            <button onClick={() => { setStep('done'); setTimeout(onSkip, 300); }} className="px-2.5 py-1 bg-white border border-gray-200 text-gray-600 rounded-lg text-[10px] font-medium hover:bg-gray-50">
              No, just client
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Select/create case */}
      {step === 'select-case' && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-blue-700 font-semibold flex items-center gap-1"><FolderOpen size={11} /> Select or create case:</span>
            <button onClick={() => setStep(selectedClientId ? 'ask-case' : 'prompt')} className="text-gray-400 hover:text-gray-600"><X size={12} /></button>
          </div>

          {clientCases.length > 0 && (
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {clientCases.map(c => (
                <button key={c.id} onClick={() => handleLinkCase(c.id)}
                  className="w-full text-left px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg text-[10px] hover:border-blue-300 hover:bg-blue-50 flex items-center gap-2">
                  <FolderOpen size={10} className="text-blue-400" />
                  <span className="font-medium text-gray-700">{c.title}</span>
                  {c.clientName && <span className="text-gray-400 ml-auto text-[9px]">{c.clientName}</span>}
                </button>
              ))}
            </div>
          )}

          <button onClick={() => setStep('new-case')} className="w-full px-2 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-[10px] font-medium hover:bg-blue-100 flex items-center justify-center gap-1">
            <Plus size={10} /> Create New Case
          </button>
          <button onClick={onSkip} className="w-full px-2 py-1 text-gray-400 text-[9px] hover:text-gray-600">Skip</button>
        </div>
      )}

      {/* Step 4b: Create new case */}
      {step === 'new-case' && (
        <div className="space-y-2">
          <div className="text-[11px] text-blue-700 font-semibold">Create new case:</div>
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Case title"
            className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-[11px]" autoFocus
            onKeyDown={e => { if (e.key === 'Enter') handleCreateCase(); }} />
          {!selectedClientId && clients.length > 0 && (
            <select value={selectedClientId} onChange={e => setSelectedClientId(e.target.value)}
              className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-[11px]">
              <option value="">— Link to client (optional) —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <div className="flex gap-1.5">
            <button onClick={handleCreateCase}
              className="px-3 py-1 bg-blue-600 text-white rounded-lg text-[10px] font-semibold">Create & Link</button>
            <button onClick={() => setStep('select-case')} className="px-3 py-1 text-gray-500 text-[10px]">Back</button>
          </div>
        </div>
      )}

      {/* Step 5: Ask about document upload */}
      {step === 'ask-document' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-green-700 font-medium">✅ Linked to case. Upload related documents?</span>
          </div>
          <div className="flex gap-1.5">
            <button onClick={() => fileRef.current?.click()} className="px-2.5 py-1 bg-green-600 text-white rounded-lg text-[10px] font-semibold hover:bg-green-700 flex items-center gap-1">
              <Upload size={10} /> Upload Document
            </button>
            <button onClick={() => { setStep('done'); setTimeout(onSkip, 300); }} className="px-2.5 py-1 bg-white border border-gray-200 text-gray-600 rounded-lg text-[10px] font-medium hover:bg-gray-50">
              Skip
            </button>
          </div>
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={(e) => {
            handleFileUpload(e);
            setStep('done');
            setTimeout(onSkip, 500);
          }} />
        </div>
      )}

      {/* Done */}
      {step === 'done' && (
        <div className="flex items-center gap-2 text-[11px] text-green-700 font-medium">
          <MessageSquare size={12} className="text-green-500" />
          ✅ Conversation saved
        </div>
      )}
    </div>
  );
};

export default ConversationLinker;
